/**
 * CheTuo Web — HTTP API client.
 *
 * Uses axios to talk to the CheTuo backend. The PC admin reuses the
 * /api/shop-mobile/* endpoints (same business logic as the Android app).
 * A dedicated /api/shop-admin/* prefix can be added later for PC-specific
 * features.
 *
 * All responses follow the unified envelope:
 *   Success: { success: true, data: T }
 *   Error:   { success: false, error: { code, message, details? }, requestId }
 */

import axios, { AxiosError, type AxiosInstance } from 'axios';

const TOKEN_KEY = 'chetuo_admin_token';
const USER_KEY = 'chetuo_admin_user';
const SHOP_ID_KEY = 'chetuo_admin_shop_id';

/** API base URL — in dev, Vite proxy forwards /api to the backend. */
const API_BASE_URL = import.meta.env['VITE_API_BASE_URL'] ?? '';

/** Shop-mobile API prefix (AGENTS.md §4, §12). */
const SHOP_MOBILE_PREFIX = '/api/shop-mobile';

/** Unified error thrown by the API client. */
export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly statusCode: number,
    public readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface SuccessEnvelope<T> {
  success: true;
  data: T;
}

interface ErrorEnvelope {
  success: false;
  error: { code: string; message: string; details?: unknown };
  requestId: string;
}

type ApiResponse<T> = SuccessEnvelope<T> | ErrorEnvelope;

const instance: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor — attach token + shopId
instance.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const shopId = localStorage.getItem(SHOP_ID_KEY);
  if (shopId) {
    config.headers['x-current-shop-id'] = shopId;
  }
  return config;
});

// Response interceptor — unwrap envelope, throw on error
instance.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ErrorEnvelope>) => {
    if (error.response?.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      window.location.href = '#/shop-admin/login';
    }
    const errData = error.response?.data;
    if (errData && !errData.success) {
      throw new ApiError(
        errData.error.code,
        errData.error.message,
        error.response?.status ?? 0,
        errData.requestId,
      );
    }
    throw new ApiError('NETWORK', error.message || '网络错误', 0);
  },
);

/** Unwrap the success envelope from a response. */
function unwrap<T>(data: unknown): T {
  const envelope = data as ApiResponse<T>;
  if (envelope && envelope.success) {
    return envelope.data;
  }
  throw new ApiError('UNKNOWN', '未知响应格式', 0);
}

/** Public API client. */
export const apiClient = {
  async get<T>(path: string): Promise<T> {
    const res = await instance.get(path);
    return unwrap<T>(res.data);
  },
  async post<T>(path: string, body?: unknown): Promise<T> {
    const res = await instance.post(path, body);
    return unwrap<T>(res.data);
  },
  async patch<T>(path: string, body?: unknown): Promise<T> {
    const res = await instance.patch(path, body);
    return unwrap<T>(res.data);
  },
  async delete<T>(path: string): Promise<T> {
    const res = await instance.delete(path);
    return unwrap<T>(res.data);
  },
};

/** Build a shop-mobile API path. */
export function shopMobilePath(suffix: string): string {
  return `${SHOP_MOBILE_PREFIX}${suffix}`;
}

/** Build an mp API path (for quick-create which is only on mp side). */
const MP_PREFIX = '/api/mp';
export function mpPath(suffix: string): string {
  return `${MP_PREFIX}${suffix}`;
}

// ── Token / user storage helpers ────────────────────────────────

export const tokenStorage = {
  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  },
  setToken(token: string): void {
    localStorage.setItem(TOKEN_KEY, token);
  },
  clearToken(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  },
  getUser<T>(): T | null {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as T) : null;
  },
  setUser(user: unknown): void {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  },
  getShopId(): string | null {
    return localStorage.getItem(SHOP_ID_KEY);
  },
  setShopId(shopId: string): void {
    localStorage.setItem(SHOP_ID_KEY, shopId);
  },
  clearShopId(): void {
    localStorage.removeItem(SHOP_ID_KEY);
  },
};
